import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Play,
  Music,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Intro from "./Intro";
import "./scroll.css";
import VidoPS from "../assets/images/videoPS.mp4";
import { searchWorshipStreams } from "../services/youtubeService";
import { getMostPopularSongs } from "../services/songService";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchState, setSearchState] = useState({
    value: "",
    results: [],
    showDropdown: false,
    page: 1,
    hasMore: false,
    isHovered: false,
    isFocused: false,
  });
  const [likedSongs, setLikedSongs] = useState({
    1: true,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
    8: false,
    9: false,
  });
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popularSongs, setPopularSongs] = useState([]);
  const [loadingPopularSongs, setLoadingPopularSongs] = useState(true);

  const songs = [
    {
      id: 1,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: true,
    },
    {
      id: 2,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 3,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 4,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 5,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 6,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 7,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 8,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
    {
      id: 9,
      title: t("home.songs.asItWas", "As It Was"),
      artist: t("home.artists.harryStyles", "Harry Styles"),
      liked: false,
    },
  ];

  const toggleLike = (id) => {
    setLikedSongs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const fetchStreams = useCallback(async () => {
    try {
      setLoading(true);
      const liveStreams = await searchWorshipStreams(4);
      setStreams(liveStreams);
      setError(null);
      localStorage.setItem("worshipStreams", JSON.stringify(liveStreams));
      localStorage.setItem("lastFetchTime", Date.now().toString());
      if (liveStreams.length > 0) {
        toast.success(
          t("youtubeStreams.loaded", { count: liveStreams.length }),
          {
            position: "bottom-right",
            duration: 2000,
          }
        );
      }
    } catch (err) {
      console.error("Error fetching YouTube streams:", err);
      const errorMessage = err.message.includes("quotaExceeded")
        ? t("youtubeStreams.quotaExceeded")
        : t("youtubeStreams.error");
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "bottom-right",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleRetry = useCallback(() => {
    setError(null);
    fetchStreams();
  }, [fetchStreams]);

  const fetchPopularSongs = useCallback(async () => {
    try {
      setLoadingPopularSongs(true);
      const songs = await getMostPopularSongs(12);
      setPopularSongs(songs);
    } catch (err) {
      console.error("Error fetching popular songs:", err);
      toast.error(t("popularSongs.error", "Failed to load popular songs"), {
        position: "bottom-right",
        duration: 3000,
      });
    } finally {
      setLoadingPopularSongs(false);
    }
  }, [t]);

  useEffect(() => {
    const cachedStreams = localStorage.getItem("worshipStreams");
    const lastFetchTime = localStorage.getItem("lastFetchTime");
    const oneDayInMs = 24 * 60 * 60 * 1000;

    if (
      cachedStreams &&
      lastFetchTime &&
      Date.now() - parseInt(lastFetchTime) < oneDayInMs
    ) {
      setStreams(JSON.parse(cachedStreams));
      setLoading(false);
    } else {
      fetchStreams();
    }

    fetchPopularSongs();
  }, [fetchStreams, fetchPopularSongs]);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSearchState((prev) => ({ ...prev, showDropdown: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const generateAvatarColor = (title) => {
    // Predefined dark color palette
    const darkColors = [
      "#1a1a1a", // Near black
      "#2d2d2d", // Dark gray
      "#1e2a44", // Dark blue
      "#2a2a2a", // Slightly lighter gray
      "#1c2526", // Dark teal-gray
    ];

    // Generate a hash from the title to select colors
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
      hash = title.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Select two colors from the palette based on the hash
    const index1 = Math.abs(hash % darkColors.length);
    const index2 = Math.abs((hash + 1) % darkColors.length);

    // Create a linear gradient with the two colors
    return `linear-gradient(45deg, ${darkColors[index1]}, ${darkColors[index2]})`;
  };

  const navigateToSong = (songID) => {
    navigate(`/songs/${songID}`);
  };

  const fontClass =
    i18n.language === "ar" ? "font-['Amiri']" : "font-['MuseoModerno']";

  return (
    <div dir={i18n.language === "ar" ? "rtl" : "ltr"} className="w-full pb-5">
      <div className="mx-auto">
        <div className="flex flex-col lg:flex-row gap-6 p-2 sm:p-4">
          {/* Hero section with video background */}
          <div className="relative rounded-3xl overflow-hidden min-h-[50vh] sm:min-h-[350px] w-full lg:w-3/4 bg-black">
            <video
              className="absolute w-full h-full object-cover opacity-80"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={VidoPS} type="video/mp4" />
              {t(
                "home.videoUnsupported",
                "Your browser does not support the video tag."
              )}
            </video>
            <div className="absolute inset-0 bg-black/40 mix-blend-lighten"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 text-center">
              <h1
                className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-2 ${fontClass}`}
              >
                PraiseSawa
              </h1>
              <div
                className={`text-lg sm:text-xl lg:text-2xl font-normal ${fontClass} tracking-wider`}
              >
                <p>{t("home.subtitle1")}</p>
                <p>{t("home.subtitle2")}</p>
              </div>
            </div>
          </div>

          {/* Live YouTube Streams sidebar */}
          <div className="w-full lg:w-1/4">
            <h2
              className={`text-lg sm:text-xl lg:text-[25px] font-normal mb-4 sm:mb-6 relative inline-block ${fontClass} ${
                i18n.language === "ar" ? "text-right" : "text-left"
              }`}
            >
              {t("youtubeStreams.live24_7", "Live 24/7 Worship Streams")}
              <span className="absolute -bottom-2 start-0 w-16 sm:w-20 h-1 bg-gray-800 rounded-full"></span>
            </h2>
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-full"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  <p
                    className={`text-red-700 text-sm sm:text-base ${fontClass}`}
                  >
                    {error}
                  </p>
                </div>
                <button
                  onClick={handleRetry}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("youtubeStreams.retry")}
                </button>
              </div>
            ) : streams.length === 0 ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                  <p
                    className={`text-yellow-700 text-sm sm:text-base ${fontClass}`}
                  >
                    {t("youtubeStreams.noStreams")}
                  </p>
                </div>
                <button
                  onClick={handleRetry}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("youtubeStreams.retry")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 pr-3 sm:px-0 lg:px:0 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                {streams.slice(0, 4).map((stream) => (
                  <motion.div
                    key={stream.id.videoId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-4 sm:gap-7"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-[200px]">
                      <img
                        src={stream.snippet.thumbnails.default?.url || ""}
                        alt={stream.snippet.title}
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full shadow-md border border-stone-900/20"
                        loading="lazy"
                      />
                      <div className="max-w-[120px] sm:max-w-[100px]">
                        <div
                          className={`text-stone-900 text-sm sm:text-base font-normal ${fontClass} ${
                            i18n.language === "ar" ? "text-right" : "text-left"
                          } line-clamp-1`}
                        >
                          {stream.snippet.title}
                        </div>
                        <div
                          className={`text-stone-900 text-xs font-light ${fontClass} ${
                            i18n.language === "ar" ? "text-right" : "text-left"
                          } line-clamp-1`}
                        >
                          {stream.snippet.channelTitle}
                        </div>
                      </div>
                    </div>
                    <div
                      className="w-24 sm:w-32 h-9 sm:h-11 bg-red-500 rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                      onClick={() =>
                        window.open(
                          `https://www.youtube.com/watch?v=${stream.id.videoId}`,
                          "_blank"
                        )
                      }
                    >
                      <Play className="text-white" size={16} />
                      <span
                        className={`text-center text-white text-sm sm:text-lg font-normal ${fontClass} tracking-wider`}
                      >
                        {t("youtubeStreams.live")}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 sm:mt-4">
          <Intro />
        </div>

        <div className="w-full px-2 sm:px-4 overflow-hidden mx-auto mt-4 sm:mt-6">
          <h2
            className={`text-lg sm:text-xl md:text-2xl lg:text-[25px] font-normal mb-4 sm:mb-6 relative inline-block ${fontClass} ${
              i18n.language === "ar" ? "text-right" : "text-left"
            }`}
          >
            {t("home.mostPopularSongs", "Most Popular Songs")}
            <span className="absolute -bottom-2 start-0 w-16 sm:w-20 h-1 bg-gray-800 rounded-full"></span>
          </h2>

          {loadingPopularSongs ? (
            // Loading skeleton for popular songs
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 xs:gap-3 sm:gap-4">
              {[...Array(12)].map((_, i) => (
                <div
                  key={`skeleton-popular-${i}`}
                  className="flex items-center gap-2 xs:gap-3 sm:gap-4 animate-pulse"
                >
                  <div className="flex gap-2 sm:gap-3">
                    <div className="w-10 h-10 xs:w-12 xs:h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl bg-gray-200"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : popularSongs.length === 0 ? (
            // No popular songs found
            <div className="text-center py-8">
              <p className={`text-gray-500 text-sm sm:text-base ${fontClass}`}>
                {t("popularSongs.none", "No popular songs found")}
              </p>
            </div>
          ) : (
            // Display popular songs
            <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 xs:gap-3 sm:gap-4">
              {popularSongs.map((song) => (
                <motion.div
                  key={song.songID}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  className={`flex items-center gap-2 xs:gap-3 sm:gap-4 ${
                    i18n.language === "ar" ? "flex-row-reverse justify-end" : "flex-row"
                  } cursor-pointer`}
                  onClick={() => navigateToSong(song.songID)}
                >
                  <div className="flex gap-2 sm:gap-3 items-center">
                    <div className="relative">
                      {/* Avatar-like image with first letter of song title */}
                      <div
                        className="w-10 h-10 xs:w-12 xs:h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl"
                        style={{ background: generateAvatarColor(song.title) }}
                      >
                        {song.title.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div
                      className={`${
                        i18n.language === "ar" ? "text-right" : "text-left"
                      } max-w-[120px] xs:max-w-[140px] sm:max-w-[160px]`}
                    >
                      <p
                        className={`text-sky-900 text-xs xs:text-sm sm:text-base font-medium ${fontClass} line-clamp-1`}
                      >
                        {song.title}
                      </p>
                      <p
                        className={`text-slate-500 text-xs font-normal ${fontClass} flex items-center gap-1 line-clamp-1`}
                      >
                        <div>Ar Song</div>
                        <span className="text-xs text-gray-400">
                          ({song.views || 0} {t("popularSongs.views", "views")})
                        </span>
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
